import { ref } from 'vue';
import { logger } from '@pkg/logger';
import { getLanguageCodeFromLocale } from '@pkg/utilities/i18n';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import type { Origin, SuggestionsApiResponse } from '@pkg/types/surpriseBag';
import { useFindSearchContext } from './useFindSearchContext';
import { useSearchParams } from './useSearchParams';

type OriginWithPlaceId = Origin & { place_id?: string };

export function useLocationAutocomplete() {
  const { locale } = useI18n();
  const route = useRoute();
  const { buildPathWithSearchParams, saveLocationToUrl, searchParams } = useSearchParams();
  const { setFindSearchContext } = useFindSearchContext();

  const suggestions = ref<OriginWithPlaceId[]>([]);
  const loadingSuggestions = ref<boolean>(false);
  const initialSearchValue = computed(() => searchParams.value.search);
  const isOnFindPage = computed(() => route.path === '/find' || route.path.endsWith('/find'));

  const internalSearch = async(searchString: string): Promise<OriginWithPlaceId[] | undefined> => {
    try {
      const data = await $fetch<SuggestionsApiResponse>('/api/location', {
        method: 'GET',
        query: {
          keyword: searchString,
          locale: getLanguageCodeFromLocale(locale.value as LOCALE_CODE),
        },
      });
      return data?.response?.success ? data?.response?.payload ?? [] : [];
    } catch (error) {
      logger.error('[useLocationAutocomplete internalSearch]: Error fetching places:', error);
      return undefined;
    }
  };

  const googleSearch = async(searchString: string): Promise<any[] | undefined> => {
    try {
      const { data: places } = await useFetch('/api/location-autocomplete-google', {
        params: { input: searchString },
      });
      return toRaw(places.value) || [];
    } catch (error) {
      logger.error('[useLocationAutocomplete googleSearch]: Error fetching places:', error);
      return undefined;
    }
  };

  const fetchSuggestions = async(searchString: string): Promise<void> => {
    loadingSuggestions.value = true;

    if (!searchString.trim()) {
      suggestions.value = [];
      loadingSuggestions.value = false;
      return;
    }

    try {
      const savedProvider = localStorage.getItem('searchProvider');

      let searchProvider: 'tgtgLocation' | 'googleLocation';

      if (savedProvider) {
        searchProvider = savedProvider as 'tgtgLocation' | 'googleLocation';
      } else {
        const { public: { flags: { finderAutocompleteApiFlag } } } = useRuntimeConfig();
        const finderAutocompleteApiFlagNumber = Number(finderAutocompleteApiFlag);
        searchProvider = Math.random() < finderAutocompleteApiFlagNumber ? 'googleLocation' : 'tgtgLocation';
        localStorage.setItem('searchProvider', searchProvider);
      }

      let autocompleteSuggestions: OriginWithPlaceId[] = [];

      if (searchProvider === 'tgtgLocation') {
        const tgtgSearch = await internalSearch(searchString);
        autocompleteSuggestions = [...(tgtgSearch || [])];
      } else {
        const googlePlacesSearch = await googleSearch(searchString);
        const transformedGooglePlacesSearch = googlePlacesSearch?.map((item: any) => ({
          ...item,
          displayName: item.address,
          place_id: item.place_id,
        })).filter((item: any) => item.displayName) || [];
        autocompleteSuggestions = [...transformedGooglePlacesSearch];
      }

      suggestions.value = autocompleteSuggestions;
      loadingSuggestions.value = false;
    } catch (error) {
      logger.error('[useLocationAutocomplete] error:', error);
      suggestions.value = [];
      loadingSuggestions.value = false;
    }
  };

  const clearSuggestions = () => {
    suggestions.value = [];
    loadingSuggestions.value = false;
  };

  const handleLocationSelected = (
    option: OriginWithPlaceId,
    searchContext?: 'header' | undefined,
  ) => {
    if (option?.center) {
      if (isOnFindPage.value) {
        if (searchContext === 'header') {
          setFindSearchContext('header');
        }
        saveLocationToUrl(option);
      } else {
        const path = buildPathWithSearchParams('/find', option, searchContext ? { searchContext } : undefined);
        navigateTo(path);
      }
    }
  };

  return {
    suggestions,
    loadingSuggestions,
    fetchSuggestions,
    clearSuggestions,
    internalSearch,
    googleSearch,
    initialSearchValue,
    handleLocationSelected,
    isOnFindPage,
  };
}
